<script>
  import { onMount, onDestroy } from "svelte";
  import { fly } from "svelte/transition";
  import InfoIcon from './icons/InfoIcon.svelte';
  import Tooltip from './Tooltip.svelte';
  import { tourFunctions } from "../tour-functions";
  
  // Props
  export let tour = null;
  export let hasDescription = false;
  
  // Local state
  let title = null;
  let description = null;
  let isVisible = false;
  let showTooltip = false;
  let currentScene = null;
  let descriptionBoxElement = null;
  
  // Update description when scene changes
  const updateSceneDescription = () => {
    const sceneId = tourFunctions.currentScene.getId();
    const scene = tour?.scenes.find(scene => scene.id === sceneId);
    if (!scene) {
      title = null;
      description = null;
      return;
    }

    currentScene = scene;

    if (!scene || !scene.description || scene.description.trim() === '') {
      title = null;
      description = null;
      return;
    }

    // Parse the content with basic HTML replacement
    let content = null;
    try {
      // Try to parse as JSON (for tiptap content)
      const contentJson = JSON.parse(scene.description);
      // For now, just use the raw text from tiptap content
      content = contentJson.content?.[0]?.content?.[0]?.text || scene.description;
    } catch (e) {
      // If not valid JSON, use as plain text with basic HTML replacement
      content = tourFunctions.parseContent(scene.description);
    }
    
    title = tourFunctions.parseContent(scene.title);
    description = content;
    
    console.log('MobileSceneDescription - Description updated:', { title, description, isVisible });
  };

  // Handle scene changes
  const handleNewPano = (e) => {
    isVisible = false; // Hide description on mobile when scene changes
    toggleDescriptionBox(false);
    updateSceneDescription();
  };

  // Initialize description on mount
  onMount(() => {
    updateSceneDescription();
    createDescriptionBox();
    window.addEventListener('krpano:newpano', handleNewPano);
  });

  onDestroy(() => {
    window.removeEventListener('krpano:newpano', handleNewPano);
    removeDescriptionBox();
  });

  // Create description box DOM element
  const createDescriptionBox = () => {
    if (descriptionBoxElement) return;
    
    descriptionBoxElement = document.createElement('div');
    descriptionBoxElement.className = 'mobile-description-box';
    descriptionBoxElement.style.cssText = `
      position: fixed;
      bottom: 60px;
      left: 0;
      right: 0;
      width: 100%;
      height: fit-content;
      max-height: 21vh;
      margin-bottom: 50px;
      background-color: rgba(0, 0, 0, 0.8);
      color: #fff;
      padding: 1rem;
      border-radius: 8px;
      font-family: var(--font-body), sans-serif;
      font-size: 14px;
      line-height: 1.5;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
      overflow-y: auto;
      pointer-events: auto;
      box-sizing: border-box;
      z-index: 998;
      display: none;
    `;
    
    // Add scrollbar styles
    const style = document.createElement('style');
    style.textContent = `
      .mobile-description-box::-webkit-scrollbar {
        width: 5px;
      }
      .mobile-description-box::-webkit-scrollbar-thumb {
        background: #ffffff;
        border-radius: 10px;
      }
      .mobile-description-box::-webkit-scrollbar-thumb:hover {
        background: #AAAAAA;
      }
    `;
    document.head.appendChild(style);
    
    document.body.appendChild(descriptionBoxElement);
  };
  
  // Update description box content
  const updateDescriptionBox = () => {
    if (!descriptionBoxElement || !title || !description) return;
    
    descriptionBoxElement.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem; border-bottom: 1px solid rgba(255, 255, 255, 0.2); padding-bottom: 0.5rem;">
        <h3 style="font-family: var(--font-heading), sans-serif; font-size: 18px; font-weight: bold; margin: 0;">
          ${title}
        </h3>
      </div>
      <div style="font-size: 13px;">
        ${description}
      </div>
    `;
  };
  
  // Show/hide description box
  const toggleDescriptionBox = (visible) => {
    if (!descriptionBoxElement) return;
    
    if (visible) {
      updateDescriptionBox();
      descriptionBoxElement.style.display = 'block';
      // Add fly-in animation
      descriptionBoxElement.style.transform = 'translateY(20px)';
      descriptionBoxElement.style.opacity = '0';
      descriptionBoxElement.style.transition = 'transform 500ms ease, opacity 500ms ease';
      
      requestAnimationFrame(() => {
        descriptionBoxElement.style.transform = 'translateY(0)';
        descriptionBoxElement.style.opacity = '1';
      });
    } else {
      descriptionBoxElement.style.transform = 'translateY(20px)';
      descriptionBoxElement.style.opacity = '0';
      setTimeout(() => {
        if (descriptionBoxElement) {
          descriptionBoxElement.style.display = 'none';
        }
      }, 500);
    }
  };
  
  // Remove description box from DOM
  const removeDescriptionBox = () => {
    if (descriptionBoxElement) {
      descriptionBoxElement.remove();
      descriptionBoxElement = null;
    }
  };

  // Event handlers
  function handleDescriptionToggle() {
    isVisible = !isVisible;
    toggleDescriptionBox(isVisible);
  }
  
  function handleMouseEnter() {
    showTooltip = true;
  }
  
  function handleMouseLeave() {
    showTooltip = false;
  }
  
  // Only show if we have description content
  $: hasDescriptionContent = hasDescription && description && description.trim() !== '';
</script>

{#if hasDescriptionContent}
  <!-- Description Toggle Button -->
  <div class="tooltip-container">
    <button
      class="description-toggle"
      on:click={handleDescriptionToggle}
      on:mouseenter={handleMouseEnter}
      on:mouseleave={handleMouseLeave}
      on:focus={handleMouseEnter}
      on:blur={handleMouseLeave}
      aria-label={description ? (isVisible ? 'Hide scene description' : 'Show scene description') : 'No description available'}
    >
      <InfoIcon />
    </button>
    
    <Tooltip 
      showingTooltip={showTooltip}
      text={description 
        ? (isVisible ? 'Hide Description' : 'Show Description')
        : 'No Description Available'}
      position="right"
    />
  </div>
{/if}

<style>
  .tooltip-container {
    position: relative;
    display: flex;
    align-items: center;
  }
  
  .description-toggle {
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .description-toggle:hover {
    opacity: 0.8;
  }
</style>
